import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import DashboardLayout from '../../layouts/DashboardLayout';
import FollowButton from '../../components/social/FollowButton';
import {
  PublicProfile,
  SocialSummary,
  getPublicProfile,
  getSocialSummary,
  blockUser,
  unblockUser,
} from '../../../services/social/api';
import { getUserPosts, ServerPost } from '../../../services/posts/api';
import { getUserTrips } from '../../../services/trips/api/tripsApi';
import ReportModal from '../../components/social/ReportModal';
import FeedPostCard from '../../components/feed/FeedPostCard';
import { toFeedPost } from '../../components/feed/toFeedPost';
import './PublicProfilePage.css';

const fallbackAvatar = `data:image/svg+xml;utf8,${encodeURIComponent(`
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">
  <defs><linearGradient id="g" x1="0" x2="1" y1="0" y2="1">
    <stop stop-color="#0d4f3c"/><stop offset="1" stop-color="#d9ff31"/>
  </linearGradient></defs>
  <rect width="128" height="128" rx="64" fill="url(#g)"/>
  <circle cx="64" cy="48" r="23" fill="#f7efe6"/>
  <path d="M24 116c7-25 22-38 40-38s33 13 40 38" fill="#f7efe6"/>
</svg>`)}`;

const PublicProfilePage: React.FC = () => {
  const { userId = '' } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = React.useState<PublicProfile | null>(null);
  const [social, setSocial] = React.useState<SocialSummary | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [posts, setPosts] = React.useState<ServerPost[]>([]);
  const [postsLocked, setPostsLocked] = React.useState(false);
  const [postsLoading, setPostsLoading] = React.useState(false);
  // Trips of theirs the VIEWER may see — the server does the filtering, so
  // this can legitimately differ between visitors. A private trip is not
  // theirs to count.
  const [tripCount, setTripCount] = React.useState(0);
  // T2.12: block state is tracked locally so the page updates without a reload
  // after Block/Unblock. Seeded from the summary on load.
  const [blockedByMe, setBlockedByMe] = React.useState(false);
  const [blockedEitherWay, setBlockedEitherWay] = React.useState(false);
  const [reportOpen, setReportOpen] = React.useState(false);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [blockBusy, setBlockBusy] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getPublicProfile(userId), getSocialSummary(userId)])
      .then(([publicProfile, summary]) => {
        if (cancelled) return;
        setProfile(publicProfile);
        setSocial(summary);
        setBlockedEitherWay(Boolean(summary.blocked || publicProfile?.blocked));
        setBlockedByMe(Boolean(summary.blockedByMe || publicProfile?.blockedByMe));
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || 'Could not load this profile.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  // Ownership is decided upstream by ProfileRoute (owner → ProfilePage), so this
  // page renders for VISITORS. During the brief pre-hydration window it can still
  // render for the owner; `social.isSelf` then just hides the follow
  // controls (below) — do NOT redirect to /profile, which now bounces back here
  // via /user/:id and would loop.

  // Their posts (T2.10a) — follower-gated server-side (403 → locked notice).
  // Depends on isFollowing so tapping Follow/Unfollow re-resolves the gate.
  React.useEffect(() => {
    if (!userId || !social || social.isSelf) return undefined;
    let cancelled = false;
    setPostsLoading(true);
    getUserPosts(userId)
      .then((result) => {
        if (cancelled) return;
        setPostsLocked(Boolean(result.forbidden));
        setPosts(result.posts);
      })
      .catch(() => {
        if (!cancelled) setPosts([]);
      })
      .finally(() => {
        if (!cancelled) setPostsLoading(false);
      });

    getUserTrips(userId)
      .then((page) => { if (!cancelled) setTripCount(page.trips.length); })
      .catch(() => { if (!cancelled) setTripCount(0); });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, social?.isFollowing, social?.isSelf]);

  const formatPostTime = (createdAt: string): string => {
    const date = new Date(createdAt);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const displayName =
    [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') ||
    profile?.nickname ||
    'Traveler';
  const handle = profile?.nickname ? `@${profile.nickname}` : '';

  const adjustFollowers = (following: boolean) =>
    setSocial((current) =>
      current ? { ...current, followers: Math.max(0, current.followers + (following ? 1 : -1)), isFollowing: following } : current,
    );

  const handleBlock = async () => {
    if (blockBusy) return;
    setMenuOpen(false);
    setBlockBusy(true);
    try {
      await blockUser(userId);
      // Blocking severs the follow both ways and hides everything — reflect it.
      setBlockedEitherWay(true);
      setBlockedByMe(true);
      setPosts([]);
    } catch {
      // Leave the page as-is; the action simply didn't take.
    } finally {
      setBlockBusy(false);
    }
  };

  const handleUnblock = async () => {
    if (blockBusy) return;
    setBlockBusy(true);
    try {
      await unblockUser(userId);
      setBlockedEitherWay(false);
      setBlockedByMe(false);
    } catch {
      // no-op
    } finally {
      setBlockBusy(false);
    }
  };

  return (
    <DashboardLayout activeSection="feed">
      <div className="v2-public-profile">
        {loading && <p className="v2-public-profile__state">Loading profile…</p>}

        {!loading && error && (
          <div className="v2-public-profile__state v2-public-profile__state--error">
            <p>{error}</p>
            <button type="button" onClick={() => navigate(-1)}>Go back</button>
          </div>
        )}

        {!loading && !error && !profile && (
          <div className="v2-public-profile__state v2-public-profile__state--error">
            <p>This user doesn’t exist yet.</p>
            <button type="button" onClick={() => navigate('/feed')}>Back to feed</button>
          </div>
        )}

        {/* T2.12: a block in either direction collapses the profile to a shell.
            Only the blocker sees an Unblock control; the other party just sees
            an unavailable account, never that they were blocked. */}
        {!loading && !error && profile && blockedEitherWay && (
          <section className="v2-public-profile__card v2-public-profile__card--blocked">
            <p className="v2-public-profile__blocked-title">This account is unavailable</p>
            {blockedByMe && (
              <>
                <p className="v2-public-profile__blocked-note">You blocked this account.</p>
                <button
                  type="button"
                  className="v2-public-profile__unblock"
                  onClick={handleUnblock}
                  disabled={blockBusy}
                >
                  {blockBusy ? 'Unblocking…' : 'Unblock'}
                </button>
              </>
            )}
            <button type="button" onClick={() => navigate(-1)}>Go back</button>
          </section>
        )}

        {!loading && !error && profile && !blockedEitherWay && (
          <section className="v2-public-profile__card">
            <img
              className="v2-public-profile__avatar"
              src={profile.pictureUrl || fallbackAvatar}
              alt=""
              onError={(event) => {
                event.currentTarget.src = fallbackAvatar;
              }}
            />
            <div className="v2-public-profile__identity">
              <h1>{displayName}</h1>
              {handle && <p className="v2-public-profile__handle">{handle}</p>}
              {/* Trips / Followers / Following. No Friends tier any more
                  (2026-08-16) — friends and followers described the same
                  relationship, so showing both showed one number twice. */}
              <div className="v2-public-profile__stats">
                <span><strong>{tripCount}</strong> Trips</span>
                <span><strong>{social?.followers ?? 0}</strong> Followers</span>
                <span><strong>{social?.following ?? 0}</strong> Following</span>
              </div>
            </div>
            {social && !social.isSelf && (
              <div className="v2-public-profile__actions">
                <FollowButton
                  userId={userId}
                  initialFollowing={social.isFollowing}
                  initialState={social.followState}
                  onChange={adjustFollowers}
                />
                {/* Native <details> = an accessible menu with no outside-click
                    plumbing; block/report are the T2.12 UGC controls. */}
                <details
                  className="v2-public-profile__menu"
                  open={menuOpen}
                  onToggle={(event) => setMenuOpen((event.target as HTMLDetailsElement).open)}
                >
                  <summary aria-label="More options" className="v2-public-profile__menu-trigger">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                      <circle cx="12" cy="5" r="1.75" />
                      <circle cx="12" cy="12" r="1.75" />
                      <circle cx="12" cy="19" r="1.75" />
                    </svg>
                  </summary>
                  <div className="v2-public-profile__menu-list" role="menu">
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => { setMenuOpen(false); setReportOpen(true); }}
                    >
                      Report user
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      className="v2-public-profile__menu-danger"
                      onClick={handleBlock}
                      disabled={blockBusy}
                    >
                      {handle ? `Block ${handle}` : 'Block user'}
                    </button>
                  </div>
                </details>
              </div>
            )}
          </section>
        )}

        {!loading && !error && profile && !blockedEitherWay && (
          <section className="v2-public-profile__posts" aria-labelledby="public-posts-heading">
            <h2 id="public-posts-heading">{postsLocked ? 'Posts' : `Posts · ${posts.length}`}</h2>
            {postsLoading && <p className="v2-public-profile__posts-notice">Loading posts…</p>}
            {!postsLoading && postsLocked && (
              <p className="v2-public-profile__posts-notice">
                Follow {displayName} to see their posts.
              </p>
            )}
            {!postsLoading && !postsLocked && posts.length === 0 && (
              <p className="v2-public-profile__posts-notice">No posts yet.</p>
            )}
            {/* The same card the feed uses — real likes, comments and shares,
                and one implementation rather than a read-only lookalike. */}
            {!postsLoading && !postsLocked && posts.map((post) => (
              <FeedPostCard
                key={post.postId}
                post={toFeedPost(post, { name: displayName, avatarUrl: profile.pictureUrl || undefined })}
                currentUserId={social?.isSelf ? userId : undefined}
              />
            ))}
          </section>
        )}

        {reportOpen && (
          <ReportModal
            target={{ targetType: 'user', targetId: userId, label: handle || displayName }}
            onClose={() => setReportOpen(false)}
          />
        )}
      </div>
    </DashboardLayout>
  );
};

export default PublicProfilePage;
